import React from "react";

/** Terms of Use — English (Last updated: April 17, 2026) */
const TermsEnglishBody: React.FC = () => (
  <>
    <p className="text-body leading-relaxed mb-6">
      Welcome to MySipur (&quot;Platform&quot;, &quot;we&quot;, &quot;us&quot;, or &quot;our&quot;). By accessing or using our
      platform, you agree to these Terms of Use.
    </p>
    <p className="text-body leading-relaxed mb-8">If you do not agree, do not use the Platform.</p>

    <section className="mb-8">
      <h2 className="text-xl font-bold text-heading mb-2">1. Eligibility</h2>
      <p className="text-body leading-relaxed">
        You must be at least 18 years old to use this Platform. By using the Platform, you confirm that you meet this
        requirement.
      </p>
    </section>

    <section className="mb-8">
      <h2 className="text-xl font-bold text-heading mb-2">2. Description of Service</h2>
      <p className="text-body leading-relaxed">
        MySipur provides a service that allows users to create personalized digital children&apos;s books using
        user-provided content, including text and images, processed using artificial intelligence tools. The service is
        currently provided free of charge.
      </p>
    </section>

    <section className="mb-8">
      <h2 className="text-xl font-bold text-heading mb-2">3. User Content Responsibility</h2>
      <p className="text-body leading-relaxed mb-3">You are solely responsible for all content you upload, including:</p>
      <ul className="list-disc ps-6 space-y-1 text-body leading-relaxed mb-3">
        <li>Images</li>
        <li>Text</li>
        <li>Personal information</li>
      </ul>
      <p className="text-body leading-relaxed mb-3">By uploading content, you represent and warrant that:</p>
      <ul className="list-disc ps-6 space-y-1 text-body leading-relaxed">
        <li>You own all rights to the content, or have full legal permission to use it</li>
        <li>You have obtained consent from all individuals appearing in the images</li>
        <li>For minors, you are the legal guardian or have explicit permission from the legal guardian</li>
        <li>Your content does not violate any law, privacy right, or intellectual property right</li>
      </ul>
    </section>

    <section className="mb-8">
      <h2 className="text-xl font-bold text-heading mb-2">4. Use of Artificial Intelligence</h2>
      <p className="text-body leading-relaxed mb-3">By using the Platform, you acknowledge and agree that:</p>
      <ul className="list-disc ps-6 space-y-1 text-body leading-relaxed">
        <li>Your content may be processed by third-party AI providers (including OpenAI)</li>
        <li>The processing is required to generate the requested output</li>
        <li>We do not use your content to train AI models</li>
      </ul>
    </section>

    <section className="mb-8">
      <h2 className="text-xl font-bold text-heading mb-2">5. Data Storage and Deletion</h2>
      <ul className="list-disc ps-6 space-y-1 text-body leading-relaxed">
        <li>User content is stored temporarily using Cloudflare R2</li>
        <li>Content is deleted after the book creation process is completed</li>
        <li>We do not guarantee recovery of deleted content</li>
      </ul>
    </section>

    <section className="mb-8">
      <h2 className="text-xl font-bold text-heading mb-2">6. Personal Use Only</h2>
      <p className="text-body leading-relaxed mb-3">
        The Platform is intended for personal, non-commercial use only. You agree not to:
      </p>
      <ul className="list-disc ps-6 space-y-1 text-body leading-relaxed">
        <li>Sell, distribute, or commercially exploit any generated content</li>
        <li>Use the Platform for business purposes without written permission</li>
      </ul>
    </section>

    <section className="mb-8">
      <h2 className="text-xl font-bold text-heading mb-2">7. Prohibited Content</h2>
      <p className="text-body leading-relaxed mb-3">You agree not to upload content that:</p>
      <ul className="list-disc ps-6 space-y-1 text-body leading-relaxed mb-3">
        <li>Violates any law or regulation</li>
        <li>Infringes on third-party rights</li>
        <li>Is abusive, harmful, or inappropriate</li>
        <li>Contains unauthorized images of others</li>
      </ul>
      <p className="text-body leading-relaxed">We reserve the right to remove any content at our sole discretion.</p>
    </section>

    <section className="mb-8">
      <h2 className="text-xl font-bold text-heading mb-2">8. No Warranty</h2>
      <p className="text-body leading-relaxed mb-3">
        The Platform is provided &quot;AS IS&quot; and &quot;AS AVAILABLE&quot;. We make no guarantees regarding:
      </p>
      <ul className="list-disc ps-6 space-y-1 text-body leading-relaxed mb-3">
        <li>Accuracy</li>
        <li>Quality</li>
        <li>Consistency of generated content</li>
        <li>Fitness for any particular purpose</li>
      </ul>
      <p className="text-body leading-relaxed">AI-generated content may contain errors or inconsistencies.</p>
    </section>

    <section className="mb-8">
      <h2 className="text-xl font-bold text-heading mb-2">9. Limitation of Liability</h2>
      <p className="text-body leading-relaxed mb-3">To the maximum extent permitted by law:</p>
      <p className="text-body leading-relaxed mb-3">MySipur shall not be liable for:</p>
      <ul className="list-disc ps-6 space-y-1 text-body leading-relaxed mb-3">
        <li>Any damages resulting from use of the Platform</li>
        <li>Any issues related to user-uploaded content</li>
        <li>Any reliance on generated content</li>
      </ul>
      <p className="text-body leading-relaxed">Your use of the Platform is at your own risk.</p>
    </section>

    <section className="mb-8">
      <h2 className="text-xl font-bold text-heading mb-2">10. Right to Refuse or Remove Content</h2>
      <p className="text-body leading-relaxed mb-3">We reserve the right to:</p>
      <ul className="list-disc ps-6 space-y-1 text-body leading-relaxed mb-3">
        <li>Refuse service</li>
        <li>Remove content</li>
        <li>Restrict access</li>
      </ul>
      <p className="text-body leading-relaxed">At our sole discretion, without notice.</p>
    </section>

    <section className="mb-8">
      <h2 className="text-xl font-bold text-heading mb-2">11. Intellectual Property</h2>
      <ul className="list-disc ps-6 space-y-1 text-body leading-relaxed">
        <li>You retain ownership of your uploaded content</li>
        <li>We do not claim ownership over your content</li>
        <li>We do not use your content for marketing or commercial purposes</li>
      </ul>
    </section>

    <section className="mb-8">
      <h2 className="text-xl font-bold text-heading mb-2">12. Changes to the Service</h2>
      <p className="text-body leading-relaxed mb-3">We may:</p>
      <ul className="list-disc ps-6 space-y-1 text-body leading-relaxed">
        <li>Modify or discontinue the Platform</li>
        <li>Change features or functionality</li>
      </ul>
      <p className="text-body leading-relaxed mt-3">At any time without notice.</p>
    </section>

    <section className="mb-8">
      <h2 className="text-xl font-bold text-heading mb-2">13. Governing Law</h2>
      <p className="text-body leading-relaxed">
        These Terms are governed by the laws of the State of Florida, United States.
      </p>
    </section>

    <section className="mb-8">
      <h2 className="text-xl font-bold text-heading mb-2">14. International Use</h2>
      <p className="text-body leading-relaxed">
        Users accessing the Platform from outside the United States do so at their own risk and are responsible for
        compliance with local laws.
      </p>
    </section>

    <section className="mb-8">
      <h2 className="text-xl font-bold text-heading mb-2">15. Contact</h2>
      <p className="text-body leading-relaxed">
        For any questions, contact us at:{" "}
        <a
          className="font-semibold text-brandNavy underline underline-offset-2 hover:text-brandOrange focus:outline-none focus:ring-2 focus:ring-brandOrange rounded"
          href="mailto:support@mysipur.app"
        >
          support@mysipur.app
        </a>
      </p>
    </section>
  </>
);

export default TermsEnglishBody;
